'use client';

import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FaChevronRight } from 'react-icons/fa';
import { useLanguage } from '../../contexts/LanguageContext';
import { fetchAllDiscounts } from '../../services/apiService';
import { getProductImageUrl as getProductImageUrlUtil } from '../../utils/imageUtils';
import { getPriceShape, formatCHF } from '../../utils/priceUtils';
import { setClickSource } from '../../services/analytics/analyticsService';

const ACCENT = 'rgb(var(--sale))'; // offers use the sale colour
// How many deals to pull for the ticker. Kept small — this is a teaser; the
// full list lives in the "Our Offers" section and the discount listing page.
const MAX_DEALS = 12;

const productTitle = (article) => {
  const folder = article.consumerFolder;
  const fromFolder = Array.isArray(folder) ? folder[0]?.title : folder?.title;
  return fromFolder || article.description?.longDescription || '';
};

/**
 * A slim, full-width deals band: live discounted products in a seamless
 * auto-scrolling ticker. Renders nothing while loading or when there are no
 * active discounts, so the page is never affected when there's nothing to
 * promote.
 *
 * Reuses the same data + price primitives as the standalone Discount section so
 * percentages and prices stay consistent across the page.
 *
 * It used to also have a 'dock' variant that overlaid the bottom edge of the
 * hero. The hero is a full-bleed split now — a dock would have run across the
 * seam between its two halves — so the band is the only form.
 */
const HeroDiscountTicker = React.memo(() => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const prefersReduced = useReducedMotion();
  const [deals, setDeals] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 'none' → standalone (non-campaign) discounts, mirroring Discount.js.
        const res = await fetchAllDiscounts(1, MAX_DEALS, 'none');
        if (cancelled) return;
        const products = res?.success ? res.data?.products || [] : [];

        const usable = products
          .map((product) => {
            const article = product?.articles?.[0];
            if (!article) return null;
            const priceShape = getPriceShape(article);
            if (!priceShape.inDiscount || priceShape.discountPercentage == null) return null;
            return {
              pharmacode: article.pharmacode,
              title: productTitle(article),
              imageUrl: getProductImageUrlUtil(article, 'F'),
              currentPrice: priceShape.currentPrice,
              basePrice: priceShape.basePrice,
              percentage: priceShape.discountPercentage,
            };
          })
          .filter(Boolean);

        setDeals(usable);
      } catch {
        if (!cancelled) setDeals([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (deals.length === 0) return null;

  const Chip = ({ deal }) => (
    <button
      type="button"
      onClick={() => { setClickSource('home_discounts'); navigate(`/product/${deal.pharmacode}`); }}
      className="group flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-full bg-white/95 border border-gray-200/70 shadow-sm hover:shadow-md hover:border-brand/40 transition-all whitespace-nowrap flex-shrink-0"
      aria-label={deal.title}
    >
      <span className="flex-shrink-0 w-9 h-9 rounded-full overflow-hidden bg-white border border-gray-100 flex items-center justify-center">
        {deal.imageUrl ? (
          <img
            src={deal.imageUrl}
            alt=""
            aria-hidden="true"
            className="max-h-full max-w-full object-contain"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <span className="w-full h-full bg-gray-50" aria-hidden="true" />
        )}
      </span>

      <span className="flex flex-col items-start leading-tight">
        <span className="text-[11px] font-semibold text-gray-800 max-w-[130px] truncate">
          {deal.title}
        </span>
        <span className="text-[11px]">
          {deal.currentPrice != null && (
            <span className="font-bold" style={{ color: ACCENT }}>
              CHF {formatCHF(deal.currentPrice)}
            </span>
          )}
          {deal.basePrice != null && deal.currentPrice != null && deal.basePrice > deal.currentPrice && (
            <span className="ml-1 text-gray-400 line-through">CHF {formatCHF(deal.basePrice)}</span>
          )}
        </span>
      </span>

      <span
        className="ml-0.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold text-white shadow-sm"
        style={{ background: ACCENT }}
      >
        -{deal.percentage}%
      </span>
    </button>
  );

  // Two identical groups so the -50% translate loops seamlessly. The second
  // group is aria-hidden to avoid duplicate announcements for screen readers.
  const Group = ({ hidden }) => (
    <div className="flex items-center gap-2.5 pr-2.5" aria-hidden={hidden || undefined}>
      {deals.map((deal, i) => (
        <Chip key={`${deal.pharmacode || i}-${hidden ? 'b' : 'a'}`} deal={deal} />
      ))}
    </div>
  );

  // Ease in when the (async-loaded) deals arrive so the bar glides into place
  // instead of popping.
  const enterY = prefersReduced ? 0 : -28;

  return (
    <motion.div
      className="relative w-full"
      aria-label={t('home.discount.dealsAria') || 'Current offers'}
      initial={{ opacity: 0, y: enterY }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <style>{`
        .hero-ticker-track { animation: hero-ticker-scroll 40s linear infinite; }
        .hero-ticker-track:hover { animation-play-state: paused; }
        @keyframes hero-ticker-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
        .hero-ticker-mask {
          -webkit-mask-image: linear-gradient(to right, transparent, #000 4%, #000 96%, transparent);
          mask-image: linear-gradient(to right, transparent, #000 4%, #000 96%, transparent);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-ticker-track { animation: none; }
          .hero-ticker-mask { overflow-x: auto; -webkit-mask-image: none; mask-image: none; }
        }
      `}</style>

      <div className="bg-white border-y border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center gap-2 sm:gap-3 px-3 sm:px-6 py-2.5">
          {/* Label */}
          <span
            className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-full text-white text-[11px] font-bold uppercase tracking-wide shadow flex-shrink-0"
            style={{ background: ACCENT }}
          >
            <span aria-hidden="true">🔥</span>
            <span className="hidden sm:inline">{t('home.discount.dealsTag') || 'Angebote'}</span>
          </span>

          {/* Ticker viewport */}
          <div className="relative flex-1 min-w-0 overflow-hidden hero-ticker-mask">
            <div className="hero-ticker-track flex items-center w-max">
              <Group />
              <Group hidden />
            </div>
          </div>

          {/* View all */}
          <button
            type="button"
            onClick={() => navigate('/products?view=discount')}
            className="group flex items-center gap-1 text-xs font-semibold flex-shrink-0 transition-colors"
            style={{ color: ACCENT }}
            aria-label={t('home.discount.viewAll') || 'View all offers'}
          >
            <span className="hidden sm:inline">{t('home.discount.viewAll') || 'View all offers'}</span>
            <FaChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
          </button>
        </div>
      </div>
    </motion.div>
  );
});

HeroDiscountTicker.displayName = 'HeroDiscountTicker';

export default HeroDiscountTicker;
